import React, { useState, useEffect } from 'react';
import { ShirtIcon, Shirt, Timer, CircleDollarSign, Star, Sparkles, ArrowRight, CheckCircle, ChevronDown, ChevronUp, Backpack, Droplet, Clock, Truck, Home, Users, Leaf } from 'lucide-react';

import DryCleaning from '../../assets/images/services/dry_cleaning.jpg';
import SteamIroning from '../../assets/images/services/steam_ironing.jpg';
import WashAndIron from '../../assets/images/services/wash_iron.jpg';
import WashAndFold from '../../assets/images/services/wash_fold.jpg';
import CarpetCleaning from '../../assets/images/services/carpet_cleaning.jpg';
import CurtainCleaning from '../../assets/images/services/curtain_cleaning.jpg';
import ShoeCleaning from '../../assets/images/services/shoe_cleaning.jpg';
import LeatherCleaning from '../../assets/images/services/leather_cleaning.jpg';
import QuoteModal from './QuoteModal';

const ServiceCard = ({ icon, image, title, description, features, detailedDescription, onSelect, isActive, onBookNow }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <div
            className={`relative transition-all duration-300 ${isActive ? 'bg-white shadow-xl rounded-lg scale-100 z-10' : 'bg-gray-50 shadow-md rounded-lg scale-95 hover:scale-97 hover:shadow-lg'}`}
            onClick={() => onSelect()}
        >
            {isActive && (
                <div className="absolute -top-2 left-0 right-0 flex justify-center">
                    <span className="px-4 py-1 bg-orange-500 text-white text-xs font-medium rounded-full">
                        Popular Choice
                    </span>
                </div>
            )}

            <div className="overflow-hidden rounded-t-lg h-48">
                <img
                    src={image || "/api/placeholder/400/320"}
                    alt={title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
            </div>

            <div className="p-6">
                <div className={`p-4 rounded-full inline-block mb-4 transition-colors ${isActive ? 'bg-blue-600 text-white' : 'bg-orange-100 text-orange-700'}`}>
                    {icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{title}</h3>
                <p className="text-gray-600 mb-4">{description}</p>

                <div className="space-y-3 mb-5">
                    {features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-start">
                            <CheckCircle size={18} className={`${isActive ? 'text-orange-500' : 'text-blue-500'} mr-2 mt-1 flex-shrink-0`} />
                            <span className="text-gray-700">{feature}</span>
                        </div>
                    ))}

                    {isExpanded && (
                        <>
                            {features.slice(3).map((feature, index) => (
                                <div key={index + 3} className="flex items-start">
                                    <CheckCircle size={18} className={`${isActive ? 'text-orange-500' : 'text-blue-500'} mr-2 mt-1 flex-shrink-0`} />
                                    <span className="text-gray-700">{feature}</span>
                                </div>
                            ))}

                            <div className="pt-4 mt-4 border-t border-gray-100">
                                <h4 className="font-bold text-gray-800 mb-3">Our Process</h4>
                                <p className="text-gray-600 mb-4">{detailedDescription}</p>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex justify-between items-center">
                    <button
                        onClick={toggleExpand}
                        className="text-blue-700 font-medium inline-flex items-center hover:underline"
                    >
                        {isExpanded ? (
                            <>Show Less <ChevronUp size={16} className="ml-1" /></>
                        ) : (
                            <>Learn More <ChevronDown size={16} className="ml-1" /></>
                        )}
                    </button>

                    <button
                        className={`px-4 py-2 ${isActive ? 'bg-orange-500 text-white' : 'border border-blue-700 text-blue-700'} rounded-full text-sm font-medium hover:bg-blue-700 hover:text-white transition-colors`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onBookNow(title);
                        }}
                    >
                        Book Now
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProcessStep = ({ number, title, description, isActive, onClick }) => {
    return (
        <div
            className={`relative p-6 rounded-lg transition-all duration-300 ${isActive ? 'bg-blue-700 text-white shadow-lg' : 'bg-white text-gray-800 hover:bg-blue-50'}`}
            onClick={onClick}
        >
            <div className="flex items-start">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${isActive ? 'bg-white text-blue-700' : 'bg-orange-100 text-orange-700'} mr-4`}>
                    {number}
                </div>
                <div>
                    <h4 className={`text-lg font-bold mb-2 ${isActive ? 'text-white' : 'text-gray-800'}`}>{title}</h4>
                    <p className={isActive ? 'text-blue-100' : 'text-gray-600'}>{description}</p>
                </div>
            </div>
        </div>
    );
};

const Services = () => {
    const [activeService, setActiveService] = useState(0); // Set to 0 for Dry Cleaning as default popular choice
    const [activeStep, setActiveStep] = useState(0);
    const [isInView, setIsInView] = useState(false);
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const [selectedServiceForModal, setSelectedServiceForModal] = useState(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                }
            },
            { threshold: 0.1 }
        );

        const section = document.getElementById('services');
        if (section) {
            observer.observe(section);
        }

        return () => {
            if (section) {
                observer.unobserve(section);
            }
        };
    }, []);

    useEffect(() => {
        if (isInView) {
            const interval = setInterval(() => {
                setActiveStep((prev) => (prev + 1) % processSteps.length);
            }, 3000);

            return () => clearInterval(interval);
        }
    }, [isInView]);

    const services = [
        {
            icon: <Sparkles size={32} />,
            image: DryCleaning,
            title: "Dry Cleaning",
            description: "Expert care for your delicate garments using modern, eco-friendly cleaning techniques.",
            features: [
                "Professional stain removal",
                "Gentle cleaning for delicate fabrics",
                "Environmentally friendly solvents",
                "Careful pressing and finishing",
                "Spot treatment for tough stains",
                "Odor removal treatment",
                "Protective packaging for transport"
            ],
            detailedDescription: "Our dry cleaning process uses safe, environmentally-friendly solvents to clean garments that can't be washed with water. Each item receives personalized attention, with specific stain treatments applied before cleaning. After cleaning, garments are expertly pressed and finished to restore their original appearance."
        },
        {
            icon: <Droplet size={32} />,
            image: SteamIroning,
            title: "Steam Ironing",
            description: "Professional ironing and pressing services that give your garments that crisp, finished look.",
            features: [
                "Expert pressing for all garment types",
                "Steam treatment for stubborn wrinkles",
                "Temperature control for delicate fabrics",
                "Shirt folding or hanging options",
                "Starching available upon request",
                "Precise collar and cuff finishing",
                "Same-day service available"
            ],
            detailedDescription: "Our steam ironing service uses commercial-grade equipment to achieve results that are difficult to match at home. We control temperature and steam levels based on fabric type to ensure safety while removing wrinkles. Special attention is given to collars, cuffs, and pleats for a perfectly finished look."
        },
        {
            icon: <ShirtIcon size={32} />,
            image: WashAndIron,
            title: "Wash & Iron",
            description: "Complete washing and professional ironing service for your everyday clothing items.",
            features: [
                "Full wash service with premium detergents",
                "Color and fabric-appropriate washing",
                "Professional ironing for crisp finish",
                "Stain treatment included",
                "Folding or hanging as preferred",
                "Quick turnaround options available",
                "Perfect for business and casual wear"
            ],
            detailedDescription: "Our wash and iron service provides a complete solution for busy professionals. Garments are washed using high-quality detergents and fabric conditioners, then professionally ironed to give them that perfect, crisp appearance. All items are returned on hangers or folded according to your preference."
        },
        {
            icon: <Backpack size={32} />,
            image: WashAndFold,
            title: "Wash & Fold",
            description: "Professional washing and precision folding for all your everyday clothing items.",
            features: [
                "Full wash, dry and fold service",
                "Temperature-controlled washing",
                "Eco-friendly detergent options",
                "Color separation and specialized care",
                "Neatly folded and packaged returns",
                "24-hour turnaround option available",
                "Subscription plans for regular service"
            ],
            detailedDescription: "Our wash and fold service begins with careful sorting by color and fabric type. We use premium detergents and fabric softeners while controlling water temperature to protect your clothes. After washing, items are precisely folded or hung according to their type and packaged neatly for return."
        },
        {
            icon: <Home size={32} />,
            image: CarpetCleaning,
            title: "Carpet Cleaning",
            description: "Professional deep cleaning for carpets and rugs to remove stains, odors and allergens.",
            features: [
                "Deep extraction cleaning",
                "Spot and stain treatment",
                "Allergen removal process",
                "Odor neutralization",
                "Fabric protection application",
                "Quick-dry technology",
                "Safe for children and pets"
            ],
            detailedDescription: "Our carpet cleaning service uses hot water extraction and eco-friendly cleaning solutions to remove deep-seated dirt, stains, and allergens. Each carpet receives pre-treatment for spots and high-traffic areas before the main cleaning process. We finish with a protective treatment to help prevent future staining."
        },
        {
            icon: <Sparkles size={32} />,
            image: CurtainCleaning,
            title: "Curtain Cleaning",
            description: "Specialized cleaning for all types of curtains and drapes, including delicate fabrics.",
            features: [
                "On-site or removal cleaning options",
                "Dust and allergen removal",
                "Stain and odor treatment",
                "Gentle process for delicate fabrics",
                "Flame retardant reapplication",
                "Precise re-hanging service",
                "Protective treatments available"
            ],
            detailedDescription: "Our curtain cleaning service can be performed with the curtains in place or by removing them for off-site cleaning. We use specialized equipment and gentle cleaning agents to remove dust, allergens, and stains without damaging delicate fabrics. All curtains are carefully finished and, if removed, professionally rehung."
        },
        {
            icon: <Star size={32} />,
            image: ShoeCleaning,
            title: "Shoe Cleaning",
            description: "Specialized cleaning for all types of footwear, from sneakers to leather shoes and boots.",
            features: [
                "Deep cleaning for all shoe types",
                "Stain and odor removal",
                "Color restoration",
                "Polishing and conditioning",
                "Sole and insole treatment",
                "Waterproofing services",
                "Minor repair options"
            ],
            detailedDescription: "Our shoe cleaning service includes thorough cleaning of the exterior and interior of your footwear. We use specialized products for different materials such as leather, suede, canvas, and athletic shoes. Each pair receives tailored treatment to remove stains, odors, and restore appearance while extending the life of your shoes."
        },
        {
            icon: <CircleDollarSign size={32} />,
            image: LeatherCleaning,
            title: "Leather Cleaning",
            description: "Expert cleaning and conditioning for leather garments, bags, and accessories.",
            features: [
                "Gentle cleaning for all leather types",
                "Stain and spot treatment",
                "Color restoration and touch-up",
                "Deep conditioning treatment",
                "Repair of minor scratches",
                "Waterproofing protection",
                "Deodorizing treatment"
            ],
            detailedDescription: "Our leather cleaning service provides specialized care for all leather items. We use gentle cleaning methods followed by professional conditioning to restore suppleness and prevent cracking. Color touch-up and repair services are available for minor damage, and all items receive a protective treatment to extend their life."
        }
    ];

    const processSteps = [
        {
            number: "01",
            title: "Sorting & Inspection",
            description: "We carefully sort your items by color, fabric type, and care requirements, inspecting for stains or special needs."
        },
        {
            number: "02",
            title: "Pre-Treatment",
            description: "Stains and heavily soiled areas receive targeted pre-treatment with appropriate spot cleaners before washing."
        },
        {
            number: "03",
            title: "Washing",
            description: "Using professional equipment and premium detergents, we wash your items at optimal temperatures for each fabric type."
        },
        {
            number: "04",
            title: "Drying & Finishing",
            description: "Garments are carefully dried at appropriate temperatures, then finished according to their requirements."
        },
        {
            number: "05",
            title: "Quality Control",
            description: "Each item is inspected for cleanliness, proper finishing, and completeness before packaging."
        },
        {
            number: "06",
            title: "Packaging & Delivery",
            description: "Clean items are neatly folded or hung, packaged protectively, and delivered to you on schedule."
        }
    ];

    // How It Works flow steps
    const serviceFlowSteps = [
        {
            icon: <Clock size={32} />,
            title: "Schedule",
            description: "Book a convenient pickup time through our app or website"
        },
        {
            icon: <Truck size={32} />,
            title: "Pickup",
            description: "We collect your items directly from your doorstep"
        },
        {
            icon: <ShirtIcon size={32} />,
            title: "Tagging",
            description: "Your items are tagged and tracked throughout the process"
        },
        {
            icon: <Backpack size={32} />,
            title: "Service",
            description: "Your clothes receive the appropriate cleaning treatment"
        },
        {
            icon: <Home size={32} />,
            title: "Delivery",
            description: "We return your fresh clothes right to your door"
        }
    ];

    // Service names array for the QuoteModal
    const serviceNames = services.map(service => service.title);

    const openQuoteModal = (serviceName = null) => {
        if (serviceName) {
            setSelectedServiceForModal(serviceName);
            // Pre-select the service in the modal
            if (!Array.isArray(selectedServiceForModal)) {
                setSelectedServiceForModal([serviceName]);
            } else if (!selectedServiceForModal.includes(serviceName)) {
                setSelectedServiceForModal([...selectedServiceForModal, serviceName]);
            }
        }
        setShowQuoteModal(true);
    };

    return (
        <>
            <section id="services" className="py-16 md:py-24 bg-gray-50">
                <div className="container mx-auto px-4">
                    {/* Section Header */}
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">Professional Laundry Solutions</h2>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            We provide comprehensive laundry and garment care services to keep your clothing
                            looking its best while saving you time and effort. Our services are tailored
                            to meet your specific needs with professional results.
                        </p>
                    </div>

                    {/* Services Grid - 4 per row, 2 rows */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
                        {services.map((service, index) => (
                            <ServiceCard
                                key={index}
                                icon={service.icon}
                                image={service.image}
                                title={service.title}
                                description={service.description}
                                features={service.features}
                                detailedDescription={service.detailedDescription}
                                isActive={activeService === index}
                                onSelect={() => setActiveService(index)}
                                onBookNow={(title) => openQuoteModal(title)}
                            />
                        ))}
                    </div>

                    {/* Our Process Section */}
                    <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-16">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">Our Cleaning Process</h3>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                                We follow a proven process to ensure your garments receive proper care at every step
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {processSteps.map((step, index) => (
                                <ProcessStep
                                    key={index}
                                    number={step.number}
                                    title={step.title}
                                    description={step.description}
                                    isActive={activeStep === index}
                                    onClick={() => setActiveStep(index)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Laundry Process Flow - 5 in a row */}
                    <div className="bg-blue-700 rounded-xl shadow-lg p-8 md:p-12 text-white">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl md:text-3xl font-bold mb-4">How It Works</h3>
                            <p className="text-lg opacity-90 max-w-3xl mx-auto">
                                Our simple 5-step process makes laundry day effortless
                            </p>
                        </div>

                        {/* Desktop Process Flow - 5 in a row */}
                        <div className="hidden md:flex items-center justify-between relative mb-6">
                            {/* Connecting Line */}
                            <div className="absolute top-1/2 left-16 right-16 h-1 bg-blue-500 -z-10"></div>

                            {serviceFlowSteps.map((step, index) => (
                                <React.Fragment key={index}>
                                    <div className="flex flex-col items-center text-center z-10">
                                        <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-6 shadow-lg">
                                            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white">
                                                {step.icon}
                                            </div>
                                        </div>
                                        <h4 className="text-xl font-bold mb-2">{step.title}</h4>
                                        <p className="text-blue-100 max-w-xs">{step.description}</p>
                                    </div>

                                    {/* Add animated arrow after each step except the last one */}
                                    {index < serviceFlowSteps.length - 1 && (
                                        <div className="animate-pulse">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                                <polyline points="12 5 19 12 12 19"></polyline>
                                            </svg>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Mobile Process Flow */}
                        <div className="md:hidden">
                            {serviceFlowSteps.map((step, index) => (
                                <div key={index} className="flex flex-col items-center text-center mb-10">
                                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 shadow-lg">
                                        <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center text-white">
                                            {step.icon}
                                        </div>
                                    </div>
                                    <h4 className="text-xl font-bold mb-2">{step.title}</h4>
                                    <p className="text-blue-100">{step.description}</p>

                                    {/* Down Arrow except for last item */}
                                    {index < serviceFlowSteps.length - 1 && (
                                        <div className="flex justify-center w-full my-4 animate-bounce">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                                <polyline points="19 12 12 19 5 12"></polyline>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Contact CTA */}
                    <div className="text-center mt-16 mb-20">
                        <p className="text-lg text-gray-600 mb-6">
                            Experience the difference professional laundry services can make in your daily life
                        </p>
                        <button
                            onClick={() => openQuoteModal()}
                            className="px-8 py-4 bg-orange-500 text-white rounded-full font-medium hover:bg-blue-700 transition-colors inline-flex items-center cursor-pointer"
                        >
                            Schedule Your First Pickup <ArrowRight size={20} className="ml-2" />
                        </button>
                    </div>

                    {/* Core Values Section */}
                    <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mt-8">
                        <div className="text-center mb-10">
                            <h3 className="text-2xl md:text-3xl font-bold text-blue-600 mb-8">OUR CORE VALUES</h3>
                            {/* <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">Our Core Values</h3> */}

                            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
                                At <span className="text-orange-500">Sparkle X</span><span className="text-blue-600">xpress</span> Laundry, we believe in more than just clean clothes.
                                We're committed to delivering care, consistency, and convenience - every time.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 mt-12">
                            {/* Customer First */}
                            <div className="flex flex-col items-center text-center">
                                <div className="text-blue-600 mb-4">
                                    <Users size={80} />
                                </div>
                                <h3 className="text-xl font-bold text-orange-500 mb-2">Customer First</h3>
                                <p className="text-blue-600">Your satisfaction is our top priority.</p>
                            </div>

                            {/* Quality & Precision */}
                            <div className="flex flex-col items-center text-center">
                                <div className="text-blue-600 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                        <path d="m9 12 2 2 4-4"></path>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-orange-500 mb-2">Quality & Precision</h3>
                                <p className="text-blue-600">We treat every garment with expert care.</p>
                            </div>

                            {/* Trust & Transparency */}
                            <div className="flex flex-col items-center text-center">
                                <div className="text-blue-600 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-handshake-icon lucide-handshake">
                                        <path d="m11 17 2 2a1 1 0 1 0 3-3" />
                                        <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
                                        <path d="m21 3 1 11h-2" />
                                        <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
                                        <path d="M3 4h8" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-orange-500 mb-2">Trust & Transparency</h3>
                                <p className="text-blue-600">No hidden fees. Clear communication.</p>
                            </div>

                            {/* Eco Responsibility */}
                            <div className="flex flex-col items-center text-center">
                                <div className="text-blue-600 mb-4">
                                    <Leaf size={80} />
                                </div>
                                <h3 className="text-xl font-bold text-orange-500 mb-2">Eco Responsibility</h3>
                                <p className="text-blue-600">We use water- and energy-efficient processes.</p>
                            </div>

                            {/* Punctuality */}
                            <div className="flex flex-col items-center text-center">
                                <div className="text-blue-600 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-orange-500 mb-2">Punctuality</h3>
                                <p className="text-blue-600">On-time pickups and deliveries, always.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use the QuoteModal component */}
            <QuoteModal
                isOpen={showQuoteModal}
                onClose={() => setShowQuoteModal(false)}
                services={serviceNames}
                selectedLocationId={1}
            />
        </>
    );
};

export default Services;